import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityData,
  ActivityDataInput,
  NewActivity
} from "@/entities/activities/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { getEnumTKey } from "@/utils/strings";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { EntityData } from "@/services/api/types";
import { useActivityModal } from "@/components/layouts/Activities/hooks/useActivityModal";
import { transformNoteActivitySubmission } from "@/components/layouts/Activities/hooks/useActivityModalSubmission";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { getMustSelectCompanyText } from "@/entities/activities/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

const NoteActivityModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}) => {
  const { t } = useTranslation();

  const [attachments, setAttachments] = useState<SavedAttachmentInfo[]>([]);
  const [attachmentsChanged, setAttachmentsChanged] = useState(false);
  const initializedActivityRef = useRef<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const {
    formRef,
    isFormValid,
    setIsFormValid,
    handleSetValue,
    isCompanySet,
    handleSubmit: baseHandleSubmit,
    getBaseFields,
    processFieldsForEditMode,
    activityData
  } = useActivityModal({
    activityType: NewActivity.NOTE,
    isOpen,
    setIsOpen,
    entityType,
    entityData,
    pageName,
    isEditMode,
    editActivityData,
    resetPage
  });

  useEffect(() => {
    if (!isOpen) {
      initializedActivityRef.current = null;
      return;
    }
    const activityKey = isEditMode ? activityData?.id : "new-note";
    if (!activityKey || initializedActivityRef.current === activityKey) return;

    setAttachments(isEditMode ? (activityData?.notes?.attachments ?? []) : []);
    setAttachmentsChanged(false);
    initializedActivityRef.current = activityKey;
  }, [activityData, isEditMode, isOpen]);

  const handleFileAttachments = (newAttachments: SavedAttachmentInfo[]) => {
    setAttachments(newAttachments);
    setAttachmentsChanged(true);
  };

  const handleUploadingChange = (uploading: boolean) => {
    setIsUploading(uploading);
  };

  const newActivityKey = getEnumTKey(
    "activities",
    "newActivities",
    NewActivity,
    NewActivity.NOTE
  );

  const formSections = useMemo<FormSectionProps[]>(() => {
    const baseFields = getBaseFields();
    const summaryField: FormFieldNewType = {
      name: "summary",
      label: t("activities.activityModal.noteContent"),
      type: "textEditor",
      required: true,
      fieldActionProps: {
        placeholder: t("activities.activityModal.noteContent"),
        autoFocus: false,
        showFocusState: true,
        minRows: 4
      }
    };
    const allFields = { ...baseFields, summary: summaryField };
    const noteFieldNames = ["date", "summary"];
    let fields = noteFieldNames
      .map((name) => allFields[name as keyof typeof allFields])
      .filter(Boolean);

    fields = processFieldsForEditMode(fields);

    return [
      {
        id: "note-form-section",
        fields
      }
    ];
  }, [getBaseFields, processFieldsForEditMode, t]);

  const handleSubmit = async (data: ActivityDataInput) => {
    await baseHandleSubmit(data, (baseData) =>
      transformNoteActivitySubmission(
        baseData,
        attachmentsChanged ? attachments : undefined,
        !isEditMode
      )
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t(`activities.activityModal.${isEditMode ? "edit" : "add"}`, {
              type: t(newActivityKey).toLowerCase(),
              activity: ""
            })}
          </Text>
        }
        content={
          <div
            className={`flex flex-col gap-3 w-[35rem] p-6 h-fit overflow-y-auto max-h-[75vh]`}
          >
            {/* Hidden element to capture initial focus */}
            <div tabIndex={0} className="sr-only" />
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
            <div className="flex flex-col gap-2">
              <Text variant="bodySecondary">
                {t("activities.activityModal.files")}
              </Text>
              <FileUpload
                key={activityData?.id ?? "new-note"}
                onAttachmentsChange={handleFileAttachments}
                onUploadingChange={handleUploadingChange}
                initialAttachments={
                  isEditMode ? activityData?.notes?.attachments : []
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  data-test="submit-button"
                  disabled={!isFormValid || isUploading || !isCompanySet}
                  onClick={handleCreateNew}
                >
                  {t(isEditMode ? "save" : "activities.activityModal.add", {
                    type: t(newActivityKey).toLowerCase(),
                    activity: ""
                  })}
                </Button>
              </TooltipTrigger>
              {!isCompanySet && (
                <TooltipContent maxWidth={15}>
                  {getMustSelectCompanyText(t, entityType)}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        }
      />
    </>
  );
};

export default NoteActivityModal;
